import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Store, IndianRupee, TrendingUp, Users, RefreshCw, ArrowUpRight, CreditCard, ScrollText } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "../vendor/Overview";
import { inr } from "@/components/app/ui";

// blue (primary), orange (secondary), green (tertiary), black, then neutrals
const COLORS = ["#2563eb", "#f97316", "#16a34a", "#0a0a0a", "#94a3b8"];
const TINT = {
  green: { card: "from-green-50 to-white border-green-100", chip: "bg-green-100 text-green-600" },
  blue: { card: "from-blue-50 to-white border-blue-100", chip: "bg-blue-100 text-blue-600" },
  orange: { card: "from-orange-50 to-white border-orange-100", chip: "bg-orange-100 text-orange-600" },
};
const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good Morning," : h < 17 ? "Good Afternoon," : "Good Evening,"; };

export default function AdminOverview() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [spin, setSpin] = useState(false);

  const load = useCallback(async () => {
    setSpin(true);
    try {
      const [o, v] = await Promise.all([api.get("/admin/overview"), api.get("/admin/vendors")]);
      setD(o.data); setVendors(v.data);
    } finally { setSpin(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="text-slate-500 text-[13px]">Loading…</div>;

  const cards = [
    { label: "Active Stores", value: d.active_stores, sub: `${d.total_stores} total`, icon: Store, color: "blue" },
    { label: "Platform GMV", value: inr(d.gmv), sub: `${d.total_orders} paid orders`, icon: IndianRupee, color: "green" },
    { label: "Monthly SaaS Revenue", value: inr(d.mrr), sub: "MRR", icon: TrendingUp, color: "orange" },
    { label: "Paid Vendors", value: d.paid_vendors, sub: `${d.free_vendors} on free plan`, icon: Users, color: "blue" },
  ];
  const quick = [
    { to: "/admin/vendors", icon: Store, label: "Vendors" },
    { to: "/admin/subscriptions", icon: CreditCard, label: "Subscriptions" },
    { to: "/admin/audit", icon: ScrollText, label: "Audit Logs" },
  ];
  const total = d.plan_distribution.reduce((n, p) => n + p.count, 0) || 1;
  const suspended = d.total_stores - d.active_stores;
  const recent = vendors.slice(0, 5);

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-[20px] font-bold text-neutral-900">Platform Overview</h1>
          <p className="text-slate-500 text-[13px]">Real-time metrics across the Sell24x7 platform.</p>
        </div>
        <button onClick={load} data-testid="admin-refresh"
          className="flex items-center gap-2 h-9 px-4 rounded-lg border border-slate-200 bg-white text-[13px] font-semibold text-neutral-800 hover:border-blue-300 hover:text-blue-600 transition-colors">
          <RefreshCw size={14} className={spin ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 space-y-4 shadow-sm">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-xl bg-gradient-to-r from-black via-neutral-950 to-blue-950 text-white px-6 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="absolute -right-10 -top-16 w-64 h-64 rounded-full bg-blue-600/25 blur-3xl" />
          <div className="relative">
            <div className="text-xs text-slate-300">{greeting()}</div>
            <div className="font-display text-2xl font-bold mt-0.5">{user?.name} <span className="inline-block vx-wave">👋</span></div>
            <div className="text-xs text-slate-300 mt-1">Here&apos;s how the platform is performing today.</div>
            <div className="mt-3 flex items-center gap-2 text-[13px] font-semibold text-green-400"><span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />{d.active_stores} active stores</div>
          </div>
          <div className="relative flex items-center gap-1 sm:border-l sm:border-white/15 sm:pl-5">
            {quick.map((q) => (
              <Link key={q.to} to={q.to} className="flex flex-col items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium text-slate-200 hover:bg-white/10 hover:text-orange-400 transition-colors">
                <q.icon size={18} /> {q.label}
              </Link>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {cards.map((c, i) => (
            <motion.div key={c.label} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 * i }}
              className={`rounded-xl border bg-gradient-to-br ${TINT[c.color].card} p-4 hover:-translate-y-0.5 hover:shadow-md transition-all`}>
              <div className="flex items-start justify-between">
                <div className="text-[13px] text-slate-500">{c.label}</div>
                <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${TINT[c.color].chip}`}><c.icon size={16} /></span>
              </div>
              <div className="text-[26px] font-bold text-neutral-900 leading-tight mt-1">{c.value}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{c.sub}</div>
            </motion.div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2.5">
          <div className="flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 bg-white text-[13px] text-neutral-800"><span className="w-2 h-2 rounded-full bg-green-500" /> Active <span className="font-bold">{d.active_stores}</span></div>
          <div className="flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 bg-white text-[13px] text-neutral-800"><span className="w-2 h-2 rounded-full bg-red-500" /> Suspended <span className="font-bold">{suspended}</span></div>
          <div className="flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 bg-white text-[13px] text-neutral-800"><span className="w-2 h-2 rounded-full bg-blue-600" /> Paid <span className="font-bold">{d.paid_vendors}</span></div>
          <div className="flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 bg-white text-[13px] text-neutral-800"><span className="w-2 h-2 rounded-full bg-slate-400" /> Free <span className="font-bold">{d.free_vendors}</span></div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
            <div>
              <h2 className="font-bold text-neutral-900 text-[15px]">Latest Stores</h2>
              <p className="text-[12px] text-slate-500">Most recently created stores</p>
            </div>
            <Link to="/admin/vendors" className="flex items-center gap-1 text-[12px] font-semibold text-blue-600 hover:underline">View all <ArrowUpRight size={13} /></Link>
          </div>
          {recent.length === 0 ? <p className="text-slate-500 text-sm p-6">No stores yet.</p> : recent.map((v) => (
            <div key={v.id} className="flex items-center justify-between px-5 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-9 h-9 rounded-full bg-orange-100 text-orange-600 text-xs font-bold flex items-center justify-center shrink-0">{v.name?.[0]?.toUpperCase()}</span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-neutral-900 truncate">{v.name}</div>
                  <div className="text-[12px] text-slate-500 truncate">{v.business_name}</div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <StatusBadge status={v.status} />
                <div className="text-[12px] text-slate-500 mt-1">{v.plan_name}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-xl bg-white border border-slate-200 shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="font-bold text-neutral-900 text-[15px]">Plan Distribution</h2>
            <p className="text-[12px] text-slate-500">Stores by subscription plan</p>
          </div>
          <div className="p-4 flex flex-wrap items-center gap-4">
            <div className="w-[190px] h-[190px] mx-auto">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={d.plan_distribution} dataKey="count" nameKey="plan" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2} isAnimationActive={false}>
                    {d.plan_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, color: "#0a0a0a", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 min-w-[160px] space-y-2.5">
              {d.plan_distribution.map((p, i) => (
                <div key={p.plan}>
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="flex items-center gap-2 text-neutral-800"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{p.plan}</span>
                    <span className="font-semibold">{p.count} <span className="text-slate-400 font-normal">({Math.round((p.count / total) * 100)}%)</span></span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(p.count / total) * 100}%`, background: COLORS[i % COLORS.length] }} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
